<template>
  <!-- Desktop dialog -->
  <el-dialog
    v-if="!isMobile"
    :model-value="modelValue"
    :close-on-click-modal="false"
    width="1000px"
    :align-center="true"
    :show-close="false"
    class="voice-select-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="dialog-header">
        <span class="dialog-title">选择文案配音</span>
        <button class="btn-close" type="button" aria-label="关闭" @click="closeDialog">
          <SvgIcon name="close-line" :size="24" />
        </button>
      </div>
    </template>

    <div class="search-bar">
      <SvgIcon name="search" :size="12" />
      <input v-model="searchKeyword" type="text" class="search-input" placeholder="搜索配音" />
    </div>

    <div class="provider-tabs">
      <button
        v-for="provider in providers"
        :key="provider.code"
        class="provider-tab"
        :class="{ active: activeProvider === provider.code }"
        type="button"
        @click="activeProvider = provider.code"
      >
        <span>{{ provider.name }}</span>
        <i
          v-if="activeProvider === provider.code"
          class="provider-tab-indicator"
          aria-hidden="true"
        ></i>
      </button>
    </div>

    <div class="category-tags">
      <button
        v-for="category in categories"
        :key="category.value"
        class="category-tag"
        :class="{ active: activeCategory === category.value }"
        type="button"
        @click="activeCategory = category.value"
      >
        {{ category.label }}
      </button>
    </div>

    <div class="voice-grid-wrap">
      <div class="voice-grid">
        <button
          v-for="voice in filteredVoices"
          :key="voice.id"
          class="voice-card"
          :class="{ active: selectedVoiceId === voice.id }"
          type="button"
          @click="selectedVoiceId = voice.id"
        >
          <span
            class="voice-avatar"
            :class="{ 'voice-avatar--playing': playingVoiceId === voice.id }"
          >
            <img :src="voice.portrait" :alt="voice.name" class="voice-avatar-img" />
            <span
              v-if="voice.profile.auditionUrl"
              class="voice-avatar-play"
              :class="{ 'voice-avatar-play--visible': playingVoiceId === voice.id }"
              @click.stop="toggleAudition(voice)"
            >
              <SvgIcon
                v-if="playingVoiceId === voice.id"
                name="book-pause"
                :size="18"
                color="#ffffff"
              />
              <SvgIcon v-else name="play" :size="18" color="#ffffff" />
            </span>
          </span>
          <span class="voice-name">{{ formatVoiceName(voice) }}</span>
          <img v-if="voice.tag" :src="voice.tag" alt="" class="voice-badge-img" />
        </button>
      </div>
    </div>

    <div class="slider-row">
      <div class="slider-group">
        <span class="slider-label">音量</span>
        <div class="slider-track-wrap">
          <el-slider
            v-model="localVolume"
            :min="0"
            :max="100"
            :step="1"
            :show-tooltip="false"
            class="voice-slider"
          />
        </div>
        <span class="slider-value">{{ localVolume }}</span>
      </div>

      <div class="slider-group">
        <span class="slider-label">语速</span>
        <div class="slider-track-wrap">
          <el-slider
            v-model="localSpeed"
            :min="0.5"
            :max="2"
            :step="0.1"
            :show-tooltip="false"
            class="voice-slider"
          />
        </div>
        <span class="slider-value">{{ formatSpeed(localSpeed) }}</span>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <button class="btn-cancel" type="button" @click="closeDialog">取消</button>
        <button class="btn-confirm" type="button" @click="handleConfirm">确定</button>
      </div>
    </template>
  </el-dialog>

  <!-- Mobile bottom sheet -->
  <Teleport to="body">
    <Transition name="voice-sheet-fade">
      <div v-if="modelValue && isMobile" class="voice-sheet-root">
        <div class="voice-sheet-overlay" @click.self="closeDialog"></div>
        <div class="voice-sheet" role="dialog" aria-label="绘本配音">
          <header v-if="!searchMode" class="voice-sheet-header">
            <span class="voice-sheet-title">绘本配音</span>
            <div class="voice-sheet-header-actions">
              <button
                class="voice-sheet-icon-btn"
                type="button"
                aria-label="搜索"
                @click="openSearchMode"
              >
                <SvgIcon name="search" :size="16" />
              </button>
              <button
                class="voice-sheet-collapse"
                type="button"
                aria-label="关闭"
                @click="closeDialog"
              >
                <span class="voice-sheet-collapse-icon" aria-hidden="true"></span>
              </button>
            </div>
          </header>

          <header v-else class="voice-sheet-search-header">
            <button
              class="voice-sheet-back"
              type="button"
              aria-label="返回"
              @click="closeSearchMode"
            >
              <span class="voice-sheet-back-icon" aria-hidden="true"></span>
            </button>
            <div class="voice-sheet-search-bar">
              <SvgIcon name="search" :size="12" />
              <input
                ref="searchInputRef"
                v-model="searchKeyword"
                type="text"
                class="voice-sheet-search-input"
                placeholder="搜索配音"
              />
            </div>
            <button
              class="voice-sheet-search-close"
              type="button"
              aria-label="关闭搜索"
              @click="closeSearchMode"
            >
              <SvgIcon name="close-line" :size="16" />
            </button>
          </header>

          <template v-if="!searchMode">
            <div class="voice-sheet-providers">
              <button
                v-for="provider in providers"
                :key="provider.code"
                class="voice-sheet-provider"
                :class="{ active: activeProvider === provider.code }"
                type="button"
                @click="activeProvider = provider.code"
              >
                {{ provider.name }}
              </button>
            </div>

            <div class="voice-sheet-categories">
              <button
                v-for="category in categories"
                :key="category.value"
                class="voice-sheet-category"
                :class="{ active: activeCategory === category.value }"
                type="button"
                @click="activeCategory = category.value"
              >
                {{ category.label }}
              </button>
            </div>
          </template>

          <div class="voice-sheet-list" :class="{ 'voice-sheet-list--search': searchMode }">
            <div
              v-if="searchMode && searchKeyword.trim() && !filteredVoices.length"
              class="voice-sheet-empty"
            >
              <span class="voice-sheet-empty-text">无搜索结果</span>
            </div>
            <button
              v-for="voice in filteredVoices"
              :key="voice.id"
              class="voice-sheet-item"
              :class="{ active: selectedVoiceId === voice.id }"
              type="button"
              @click="selectedVoiceId = voice.id"
            >
              <span
                class="voice-sheet-avatar"
                :class="{ 'voice-sheet-avatar--playing': playingVoiceId === voice.id }"
              >
                <img :src="voice.portrait" :alt="voice.name" class="voice-sheet-avatar-img" />
                <span
                  v-if="voice.profile.auditionUrl"
                  class="voice-sheet-avatar-play"
                  :class="{ 'voice-sheet-avatar-play--visible': playingVoiceId === voice.id }"
                  @click.stop="toggleAudition(voice)"
                >
                  <svg
                    v-if="playingVoiceId === voice.id"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect x="6" y="5" width="4" height="14" rx="1" fill="#ffffff" />
                    <rect x="14" y="5" width="4" height="14" rx="1" fill="#ffffff" />
                  </svg>
                  <SvgIcon v-else name="play" :size="18" color="#ffffff" />
                </span>
              </span>
              <span class="voice-sheet-name">{{ formatMobileVoiceName(voice) }}</span>
            </button>
          </div>

          <div v-if="!searchMode" class="voice-sheet-footer">
            <div class="voice-sheet-sliders">
              <div class="voice-sheet-slider-row">
                <span class="voice-sheet-slider-label">音量</span>
                <div class="voice-sheet-slider-track">
                  <el-slider
                    v-model="localVolume"
                    :min="0"
                    :max="100"
                    :step="1"
                    :show-tooltip="false"
                    class="voice-sheet-slider"
                  />
                </div>
                <span class="voice-sheet-slider-value">{{ localVolume }}</span>
              </div>
              <div class="voice-sheet-slider-row">
                <span class="voice-sheet-slider-label">语速</span>
                <div class="voice-sheet-slider-track">
                  <el-slider
                    v-model="localSpeed"
                    :min="0.5"
                    :max="2"
                    :step="0.1"
                    :show-tooltip="false"
                    class="voice-sheet-slider"
                  />
                </div>
                <span class="voice-sheet-slider-value">{{ formatSpeed(localSpeed) }}</span>
              </div>
            </div>
            <div class="voice-sheet-actions">
              <button class="voice-sheet-btn-cancel" type="button" @click="closeDialog">取消</button>
              <button class="voice-sheet-btn-confirm" type="button" @click="handleConfirm">
                确定
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { fetchTtsRoles, fetchTtsServices, type TtsRoleGroup, type TtsService } from '@/api/tts';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import AudioPlayer from '@/utils/AudioPlayer';

  export interface VoiceOption {
    id: string;
    name: string;
    description: string;
    portrait: string;
    provider: string;
    category: string;
    tag?: string | null;
    tagType?: string | null;
    profile: {
      auditionText: string;
      auditionUrl: string;
    };
  }

  export interface VoiceSelectResult {
    voice: VoiceOption;
    volume: number;
    speed: number;
  }

  const providers = ref<TtsService[]>([]);
  const roleGroups = ref<TtsRoleGroup[]>([]);

  const categories = computed(() =>
    roleGroups.value.map((group) => ({
      label: group.name,
      value: group.name
    }))
  );

  const voiceOptions = computed<VoiceOption[]>(() =>
    roleGroups.value.flatMap((group) =>
      group.list.map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description,
        portrait: role.portrait,
        provider: activeProvider.value,
        category: group.name,
        tag: role.tag,
        tagType: role.tagType,
        profile: {
          auditionText: role.profile.auditionText,
          auditionUrl: role.profile.auditionUrl
        }
      }))
    )
  );

  const props = withDefaults(
    defineProps<{
      modelValue: boolean;
      initialVoiceId?: string;
      initialVolume?: number;
      initialSpeed?: number;
    }>(),
    {
      initialVoiceId: '',
      initialVolume: 75,
      initialSpeed: 1
    }
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'confirm', result: VoiceSelectResult): void;
  }>();

  const isMobile = ref(false);
  const searchMode = ref(false);
  const searchInputRef = ref<HTMLInputElement | null>(null);
  const searchKeyword = ref('');
  const activeProvider = ref('');
  const activeCategory = ref('');
  const selectedVoiceId = ref(props.initialVoiceId);
  const localVolume = ref(props.initialVolume);
  const localSpeed = ref(props.initialSpeed);
  const playingVoiceId = ref<string | null>(null);

  const isMobileViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  const filteredVoices = computed(() => {
    if (searchMode.value) {
      const keyword = searchKeyword.value.trim().toLowerCase();
      return voiceOptions.value.filter((voice) => {
        if (!keyword) return true;
        const displayName = formatMobileVoiceName(voice).toLowerCase();
        return displayName.includes(keyword) || voice.name.toLowerCase().includes(keyword);
      });
    }

    return voiceOptions.value.filter((voice) => {
      const matchProvider = voice.provider === activeProvider.value;
      const matchCategory = voice.category === activeCategory.value;
      const keyword = searchKeyword.value.trim().toLowerCase();
      const displayName = formatVoiceName(voice).toLowerCase();
      const matchKeyword = !keyword || displayName.includes(keyword);
      return matchProvider && matchCategory && matchKeyword;
    });
  });

  const formatVoiceName = (voice: VoiceOption) =>
    voice.description ? `${voice.name} | ${voice.description}` : voice.name;

  const formatMobileVoiceName = (voice: VoiceOption) =>
    voice.description ? `${voice.name} (${voice.description})` : voice.name;

  const formatSpeed = (value: number) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const stopAudition = () => {
    AudioPlayer.instance.stop();
    playingVoiceId.value = null;
  };

  const toggleAudition = (voice: VoiceOption) => {
    selectedVoiceId.value = voice.id;

    const url = voice.profile.auditionUrl;
    if (!url) return;

    if (playingVoiceId.value === voice.id) {
      stopAudition();
      return;
    }

    const reset = () => {
      playingVoiceId.value = null;
    };
    AudioPlayer.instance.play(url, reset, reset);
    playingVoiceId.value = voice.id;
  };

  const loadProviders = async () => {
    try {
      const services = await fetchTtsServices();
      providers.value = services;
      if (!services.length) return;
      if (!services.some((item) => item.code === activeProvider.value)) {
        activeProvider.value = services[0].code;
        return;
      }
      await loadRoles();
    } catch {
      providers.value = [];
    }
  };

  const loadRoles = async () => {
    if (!activeProvider.value) return;
    try {
      const groups = await fetchTtsRoles(activeProvider.value);
      roleGroups.value = groups;
      if (!groups.length) {
        activeCategory.value = '';
        return;
      }
      if (!groups.some((group) => group.name === activeCategory.value)) {
        activeCategory.value = groups[0].name;
      }
    } catch {
      roleGroups.value = [];
      activeCategory.value = '';
    }
  };

  const openSearchMode = async () => {
    searchMode.value = true;
    searchKeyword.value = '';
    await nextTick();
    searchInputRef.value?.focus();
  };

  const closeSearchMode = () => {
    searchMode.value = false;
    searchKeyword.value = '';
  };

  const closeDialog = () => {
    stopAudition();
    searchMode.value = false;
    searchKeyword.value = '';
    emit('update:modelValue', false);
  };

  const handleConfirm = () => {
    const voice =
      voiceOptions.value.find((item) => item.id === selectedVoiceId.value) ??
      filteredVoices.value[0];
    if (!voice) return;

    stopAudition();
    emit('confirm', {
      voice,
      volume: localVolume.value,
      speed: localSpeed.value
    });
    closeDialog();
  };

  const handleViewportChange = () => {
    isMobile.value = isMobileViewport();
  };

  watch(activeProvider, () => {
    void loadRoles();
  });

  watch(
    () => props.modelValue,
    (open) => {
      if (!open) {
        stopAudition();
        searchMode.value = false;
        return;
      }
      isMobile.value = isMobileViewport();
      searchKeyword.value = '';
      searchMode.value = false;
      selectedVoiceId.value = props.initialVoiceId;
      localVolume.value = props.initialVolume;
      localSpeed.value = props.initialSpeed;
      void loadProviders();
    }
  );

  watch(filteredVoices, (voices) => {
    if (searchMode.value) return;
    if (!voices.length) return;
    if (!voices.some((voice) => voice.id === selectedVoiceId.value)) {
      selectedVoiceId.value = voices[0].id;
    }
  });

  watch([() => props.modelValue, isMobile], ([visible, mobile]) => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = visible && mobile ? 'hidden' : '';
  });

  onMounted(() => {
    isMobile.value = isMobileViewport();
    window.addEventListener('resize', handleViewportChange);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleViewportChange);
    stopAudition();
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  });
</script>

<style scoped>
  .voice-select-dialog :deep(.el-dialog) {
    border-radius: 12px;
    box-shadow:
      0 8px 8px -4px rgba(16, 24, 40, 0.03),
      0 20px 24px -4px rgba(16, 24, 40, 0.08);
  }

  .voice-select-dialog :deep(.el-dialog__header) {
    padding: 24px 24px 0;
    margin: 0;
  }

  .voice-select-dialog :deep(.el-dialog__body) {
    padding: 16px 24px 0;
  }

  .voice-select-dialog :deep(.el-dialog__footer) {
    padding: 0 24px 24px;
  }

  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .dialog-title {
    color: #333333;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  .btn-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #333940;
    cursor: pointer;
  }

  .btn-close:hover {
    background: #f1f5f9;
  }

  .search-bar {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 36px;
    margin-bottom: 16px;
    padding: 0 12px;
    border-radius: 66px;
    background: #f5f5f5;
    color: #160211;
  }

  .search-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #160211;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    line-height: 16px;
  }

  .search-input::placeholder {
    color: #6e6a7c;
  }

  .provider-tabs {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 12px;
  }

  .provider-tab {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 32px;
    padding: 0 10px 8px;
    border: 0;
    background: transparent;
    color: #666666;
    font: inherit;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    cursor: pointer;
  }

  .provider-tab.active {
    color: #333333;
    font-weight: 700;
  }

  .provider-tab-indicator {
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 26px;
    height: 4px;
    border-radius: 4px;
    background: #6033e1;
    transform: translateX(-50%);
  }

  .category-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }

  .category-tag {
    height: 32px;
    padding: 0 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    color: #333333;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    cursor: pointer;
  }

  .category-tag.active {
    border-color: transparent;
    background: #eff6ff;
    color: #6033e1;
  }

  .voice-grid-wrap {
    height: 367px;
    overflow: hidden;
  }

  .voice-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-auto-rows: 56px;
    align-content: start;
    gap: 12px;
    height: 100%;
    overflow-y: auto;
    padding-right: 4px;
  }

  .voice-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 56px;
    padding: 10px 24px;
    border: 2px solid transparent;
    border-radius: 6px;
    background: #fbf9ff;
    color: #333333;
    font: inherit;
    text-align: left;
    cursor: pointer;
    box-sizing: border-box;
  }

  .voice-card.active {
    border-color: #6033e1;
  }

  .voice-avatar {
    position: relative;
    width: 32px;
    height: 32px;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    overflow: hidden;
  }

  .voice-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .voice-avatar-play {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    inset: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .voice-card:hover .voice-avatar-play {
    opacity: 1;
  }

  .voice-card:hover .voice-avatar {
    border-radius: 50%;
  }

  .voice-avatar-play--visible {
    opacity: 1;
  }

  .voice-avatar--playing {
    border-radius: 50%;
  }

  .voice-avatar--playing .voice-avatar-img {
    filter: brightness(0.7);
  }

  .voice-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    font-size: 14px;
    line-height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .voice-badge-img {
    position: absolute;
    top: 4px;
    right: 8px;
    height: 16px;
  }

  .slider-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
    margin-top: 16px;
  }

  .slider-group {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .slider-label {
    flex: 0 0 auto;
    color: #333333;
    font-size: 14px;
    line-height: 22px;
    opacity: 0.8;
  }

  .slider-track-wrap {
    flex: 1;
    min-width: 0;
  }

  .voice-slider {
    width: 100%;
  }

  .voice-slider :deep(.el-slider__runway) {
    height: 6px;
    border-radius: 13px;
    background-color: #e4e7ed;
  }

  .voice-slider :deep(.el-slider__bar) {
    height: 6px;
    border-radius: 13px;
    background-color: #6033e1;
  }

  .voice-slider :deep(.el-slider__button) {
    width: 14px !important;
    height: 14px !important;
    border: 2px solid #6033e1;
    background-color: #ffffff;
    box-shadow: none;
  }

  .voice-slider :deep(.el-slider__button:hover),
  .voice-slider :deep(.el-slider__button:active),
  .voice-slider :deep(.el-slider__button.is-dragging) {
    transform: none;
  }

  .slider-value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 24px;
    flex: 0 0 auto;
    border-radius: 4px;
    background: #f4f5f7;
    color: #333333;
    font-size: 12px;
    line-height: 16px;
    opacity: 0.8;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .btn-cancel,
  .btn-confirm {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 24px;
    border-radius: 6px;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    cursor: pointer;
  }

  .btn-cancel {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #333333;
  }

  .btn-cancel:hover {
    border-color: #c2c2c2;
    background: #f8fafc;
  }

  .btn-confirm {
    border: 0;
    background: #6033e1;
    color: #ffffff;
  }

  .btn-confirm:hover {
    background: #7c5cec;
  }

  /* ============================================================
   Mobile bottom sheet
   ============================================================ */
  .voice-sheet-root {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .voice-sheet-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
  }

  .voice-sheet {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: min(780px, 92dvh);
    padding: 16px 16px 0;
    border-radius: 16px 16px 0 0;
    background: #ffffff;
    box-sizing: border-box;
    overflow: hidden;
  }

  .voice-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 auto;
    height: 24px;
    margin-bottom: 16px;
  }

  .voice-sheet-title {
    color: #160211;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
  }

  .voice-sheet-header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .voice-sheet-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #160211;
    cursor: pointer;
  }

  .voice-sheet-collapse {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 24px;
    padding: 0;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #ffffff;
    cursor: pointer;
  }

  .voice-sheet-collapse-icon {
    width: 8px;
    height: 8px;
    display: block;
    border-right: 1.5px solid #160211;
    border-bottom: 1.5px solid #160211;
    transform: rotate(45deg) translateY(-2px);
  }

  .voice-sheet-search-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
    height: 36px;
    margin-bottom: 16px;
  }

  .voice-sheet-back,
  .voice-sheet-search-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    padding: 0;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #ffffff;
    color: #160211;
    cursor: pointer;
  }

  .voice-sheet-back-icon {
    width: 8px;
    height: 8px;
    display: block;
    border-left: 1.5px solid #160211;
    border-bottom: 1.5px solid #160211;
    transform: rotate(45deg);
  }

  .voice-sheet-search-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    height: 36px;
    padding: 0 12px;
    border-radius: 66px;
    background: #eff0f7;
    color: #6e6a7c;
  }

  .voice-sheet-search-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #160211;
    font: inherit;
    font-size: 13px;
    line-height: 16px;
  }

  .voice-sheet-search-input::placeholder {
    color: #6e6a7c;
  }

  .voice-sheet-providers {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 0;
    height: 44px;
    margin-bottom: 12px;
    padding: 4px;
    border-radius: 16px;
    background: #eff0f7;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .voice-sheet-providers::-webkit-scrollbar {
    display: none;
  }

  .voice-sheet-provider {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 1 0 auto;
    min-width: 88px;
    height: 36px;
    padding: 0 12px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: #160211;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
    white-space: nowrap;
    cursor: pointer;
  }

  .voice-sheet-provider.active {
    background: #ffffff;
    font-weight: 500;
    box-shadow: 0 1px 4px rgba(16, 24, 40, 0.06);
  }

  .voice-sheet-categories {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 8px;
    margin-bottom: 12px;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .voice-sheet-categories::-webkit-scrollbar {
    display: none;
  }

  .voice-sheet-category {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    height: 36px;
    padding: 0 16px;
    border: 0;
    border-radius: 16px;
    background: #ffffff;
    color: #160211;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    white-space: nowrap;
    cursor: pointer;
  }

  .voice-sheet-category.active {
    background: #ede8ff;
    color: #6033e1;
  }

  .voice-sheet-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 8px;
  }

  .voice-sheet-list--search {
    padding-bottom: 0;
  }

  .voice-sheet-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
  }

  .voice-sheet-empty-text {
    color: #6e6a7c;
    font-size: 13px;
    line-height: 16px;
  }

  .voice-sheet-item {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    height: 64px;
    padding: 16px;
    border: 2px solid transparent;
    border-radius: 16px;
    background: #fbf9ff;
    color: #160211;
    font: inherit;
    text-align: left;
    box-sizing: border-box;
    cursor: pointer;
  }

  .voice-sheet-item.active {
    border-color: #6033e1;
  }

  .voice-sheet-avatar {
    position: relative;
    width: 32px;
    height: 32px;
    flex: 0 0 auto;
    border-radius: 50%;
    overflow: hidden;
  }

  .voice-sheet-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .voice-sheet-avatar-play {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    inset: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    transition: opacity 0.15s ease;
  }

  .voice-sheet-avatar-play--visible {
    background: rgba(96, 51, 225, 0.75);
  }

  .voice-sheet-avatar--playing .voice-sheet-avatar-img {
    filter: brightness(0.7);
  }

  .voice-sheet-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .voice-sheet-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 0 auto;
    margin: 0 -16px 0;
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: #ffffff;
  }

  .voice-sheet-sliders {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .voice-sheet-slider-row {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 26px;
  }

  .voice-sheet-slider-label {
    flex: 0 0 auto;
    width: 26px;
    color: #160211;
    font-size: 13px;
    line-height: 14px;
  }

  .voice-sheet-slider-track {
    flex: 1;
    min-width: 0;
  }

  .voice-sheet-slider {
    width: 100%;
  }

  .voice-sheet-slider :deep(.el-slider__runway) {
    height: 4px;
    border-radius: 6px;
    background-color: #ede8ff;
  }

  .voice-sheet-slider :deep(.el-slider__bar) {
    height: 4px;
    border-radius: 6px;
    background-color: #6033e1;
  }

  .voice-sheet-slider :deep(.el-slider__button) {
    width: 16px !important;
    height: 16px !important;
    border: 2px solid #6033e1;
    background-color: #ffffff;
    box-shadow: none;
  }

  .voice-sheet-slider :deep(.el-slider__button:hover),
  .voice-sheet-slider :deep(.el-slider__button:active),
  .voice-sheet-slider :deep(.el-slider__button.is-dragging) {
    transform: none;
  }

  .voice-sheet-slider-value {
    flex: 0 0 auto;
    min-width: 26px;
    color: #160211;
    font-size: 13px;
    line-height: 14px;
    text-align: right;
  }

  .voice-sheet-actions {
    display: flex;
    gap: 8px;
  }

  .voice-sheet-btn-cancel,
  .voice-sheet-btn-confirm {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    height: 42px;
    border-radius: 16px;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    cursor: pointer;
  }

  .voice-sheet-btn-cancel {
    border: 1px solid #eff0f7;
    background: #ffffff;
    color: #160211;
  }

  .voice-sheet-btn-confirm {
    border: 0;
    background: #6033e1;
    color: #ffffff;
  }

  .voice-sheet-fade-enter-active,
  .voice-sheet-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .voice-sheet-fade-enter-active .voice-sheet,
  .voice-sheet-fade-leave-active .voice-sheet {
    transition: transform 0.2s ease;
  }

  .voice-sheet-fade-enter-from,
  .voice-sheet-fade-leave-to {
    opacity: 0;
  }

  .voice-sheet-fade-enter-from .voice-sheet,
  .voice-sheet-fade-leave-to .voice-sheet {
    transform: translateY(100%);
  }
</style>
